import { useMainLayout } from '../../../layouts/MainPageLayout/MainLayoutContext';
import { DisplayingRegionLevel } from '../../../../types/DisplayingRegionLevel';
import { styled } from '@/styled-system/jsx';
import { Accordion, Box, Divider, Heading } from '@zillow/constellation-10';
import { RegionStats } from './RegionStats';
import { Top5PropertiesByLead } from './Top5PropertiesByLead';
import { Top5LeadOpportunities } from './Top5LeadOpportunities';
import { type ProspectOpportunity } from './OverviewMap.hooks';
import { OverviewMapSidebarEmptyState } from './OverviewMapSidebarEmptyState';

const SidebarContent = styled(Box, {
  base: {
    padding: '400',
  },
});

export const useRegionStatsTitle = () => {
  const { selectedRegionId, regionsMapping, displayMode, visibleRegionIds } = useMainLayout();

  if (!selectedRegionId && visibleRegionIds.length === 1) {
    const { regionId } = visibleRegionIds[0];
    return regionsMapping[regionId] ? `Stats for ${regionsMapping[regionId]}` : 'Stats for region on map';
  }
  if (displayMode === DisplayingRegionLevel.COUNTRY) return 'Stats for all portfolio states';
  if (displayMode === DisplayingRegionLevel.STATE && !selectedRegionId) return 'Stats for states on map';
  if (displayMode === DisplayingRegionLevel.METRO_AREA && !selectedRegionId) return 'Stats for metro areas on map';
  if (displayMode === DisplayingRegionLevel.CITY && !selectedRegionId) return 'Stats for cities on map';
  if (displayMode === DisplayingRegionLevel.ZIP && !selectedRegionId) return 'Stats for ZIP codes on map';

  return 'Stats for regions on map';
};

export const OverviewMapSidebar = ({
  top5PropertiesByLead,
  bottom5PropertiesByLead,
}: {
  top5PropertiesByLead: Array<ProspectOpportunity>;
  bottom5PropertiesByLead: Array<ProspectOpportunity>;
}) => {
  const { selectedRegionId, displayMode, allProperties, visibleRegionIds } = useMainLayout();
  const regionStatsTitle = useRegionStatsTitle();

  const visibleProperties =
    allProperties.filter((property) =>
      visibleRegionIds.some(
        (region) =>
          region.regionId === property.propertyDetails.stateRegionId ||
          region.regionId === property.propertyDetails.metroStatRegionId ||
          region.regionId === property.propertyDetails.cityRegionId ||
          region.regionId === property.propertyDetails.zipRegionId,
      ),
    ) ?? [];
  const hasNoProperties = visibleProperties.length === 0;

  if (hasNoProperties) {
    return <OverviewMapSidebarEmptyState />;
  }

  if (displayMode === DisplayingRegionLevel.COUNTRY) {
    return (
      <SidebarContent>
        <Accordion.Root type="multiple" css={{ width: '100%' }}>
          <Accordion.Item value="accordion-1">
            <Accordion.Header>
              <Heading level={5}>{regionStatsTitle}</Heading>
            </Accordion.Header>
            <Accordion.Panel>
              <RegionStats visibleProperties={visibleProperties} />
            </Accordion.Panel>
          </Accordion.Item>
          {top5PropertiesByLead.length > 0 && bottom5PropertiesByLead.length > 0 && (
            <>
              <Accordion.Item value="accordion-2">
                <Accordion.Header>
                  <Heading level={5}>Top {top5PropertiesByLead.length} properties by prospects</Heading>
                </Accordion.Header>
                <Accordion.Panel css={{ paddingLeft: '0', paddingRight: '0' }}>
                  <Top5PropertiesByLead top5PropertiesByLead={top5PropertiesByLead} />
                </Accordion.Panel>
              </Accordion.Item>
              <Accordion.Item value="accordion-3">
                <Accordion.Header>
                  <Heading level={5}>Top {bottom5PropertiesByLead.length} prospect opportunities</Heading>
                </Accordion.Header>
                <Accordion.Panel css={{ paddingLeft: '0', paddingRight: '0' }}>
                  <Top5LeadOpportunities bottom5PropertiesByLead={bottom5PropertiesByLead} />
                </Accordion.Panel>
              </Accordion.Item>
            </>
          )}
        </Accordion.Root>
      </SidebarContent>
    );
  }

  if (!selectedRegionId && displayMode === DisplayingRegionLevel.ZIP) {
    return (
      <Box css={{ padding: '400' }}>
        <Heading level={5}>Top {top5PropertiesByLead.length} properties by leads</Heading>
        <Top5PropertiesByLead top5PropertiesByLead={top5PropertiesByLead} />
        <Divider css={{ marginInline: '-400', marginBlock: '600', width: 'calc(100% + 32px)' }} />
        <Heading level={5}>Top {bottom5PropertiesByLead.length} lead opportunities</Heading>
        <Top5LeadOpportunities bottom5PropertiesByLead={bottom5PropertiesByLead} />
      </Box>
    );
  }

  return (
    <Box css={{ padding: '400' }}>
      <RegionStats visibleProperties={visibleProperties} />
    </Box>
  );
};
